/**
 * Procurement Demo — Nami forecast overrides.
 *
 * THROWAWAY DEMO CODE. Turns a sentence like "we're gonna sell 30% more over the
 * next 6 months" into a change on the demand forecast. Three pure steps so the
 * tests can walk them without a browser: `parseOverrideRequest` reads the
 * percent, direction and months; `planOverride` finds the item, works out the
 * numbers Nami narrates and the change to apply; the interceptor applies it to
 * the forecast store after the narration, so the open page updates live.
 */

import { fmtMoneyCompact } from '../data/format';
import { getSite } from '../data/company';
import { BUCKET_COUNT } from '../pages/forecast/bom-data';
import { buildBomModel, fgRecord } from '../pages/forecast/bom-logic';
import { buildsPlanFor } from '../pages/forecast/bom-plan';
import {
  AXIS,
  DEFAULT_SERIES_ID,
  SERIES_SPECS,
  getSpec,
  specId,
} from '../pages/forecast/forecast-data';
import {
  buildView,
  formatFactorPercent,
} from '../pages/forecast/forecast-engine';
import { fmtUnits } from '../pages/forecast/forecast-format';
import { adjustForecast, sum } from '../pages/forecast/forecast-math';
import type { UpliftRequest } from '../pages/forecast/forecast-store';
import type {
  ForecastDemoState,
  SeriesSpec,
} from '../pages/forecast/forecast-types';

const DETAILS_PATH = '/procurement/forecast/details';
const BOM_PATH = '/procurement/forecast/bom';

/** The most a single request may move the forecast, in percent. */
const MAX_UPLIFT_PERCENT = 100;
const MAX_CUT_PERCENT = 90;
const DEFAULT_MONTHS = 6;

const MCC = 'PPC-MCC-480@PNL';
const PDP = 'PPC-PDP-208@PNL';
const KIT = 'KIT-VFD-50@PNL';
const FINISHED_GOOD_SERIES = [MCC, PDP, KIT];

const FINISHED_GOOD_NAME: Record<string, string> = {
  [MCC]: 'motor control panels',
  [PDP]: 'power distribution panels',
  [KIT]: 'retrofit kits',
};

// --- Reading the sentence -------------------------------------------------------------

export interface OverrideRequest {
  /** 30 reads as 30% more, -10 as 10% less. */
  percent: number;
  /** First forecast month and the one it stops before. */
  fromIndex: number;
  toIndex: number;
  /** "the next 6 months", "Q1". */
  windowText: string;
  /** The months came from the sentence, not the six-month default. */
  explicitWindow: boolean;
}

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

const PERCENT_WORDS: Record<string, number> = {
  ten: 10,
  fifteen: 15,
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
};

const UP_WORDS =
  /\b(more|up|higher|increase[ds]?|bump|raise[ds]?|boost|grow|growth|lift|extra|add|plus)\b/;
const DOWN_WORDS =
  /\b(less|fewer|lower|down|decrease[ds]?|cut|reduce[ds]?|drop|shrink|fall|minus|slash)\b/;
const SUBJECT_WORDS =
  /\b(forecasts?|demand|sell|selling|sold|sales|volumes?|builds?|usage|orders)\b/;

/** Lower case, straight quotes, single spaces. */
function clean(message: string): string {
  return message
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function readPercent(text: string): { value: number; sign: string } | null {
  const numeric =
    /(?<![\w.])([+-]?)\s?(\d+(?:\.\d+)?)\s?(?:%|percent|per cent|pct)/.exec(
      text,
    );
  if (numeric?.[2])
    return { value: Number(numeric[2]), sign: numeric[1] ?? '' };
  const word = /\b(ten|fifteen|twenty|thirty|forty|fifty)\s+(?:%|percent)/.exec(
    text,
  );
  const value = word?.[1] ? PERCENT_WORDS[word[1]] : undefined;
  return value === undefined ? null : { value, sign: '' };
}

/** +1 for more, -1 for less; the word that comes first wins, a sign counts too. */
function readDirection(text: string, sign: string): 1 | -1 | null {
  if (sign === '+') return 1;
  if (sign === '-') return -1;
  const up = UP_WORDS.exec(text)?.index;
  const down = DOWN_WORDS.exec(text)?.index;
  if (up === undefined && down === undefined) return null;
  if (down === undefined) return 1;
  if (up === undefined) return -1;
  return down < up ? -1 : 1;
}

function monthsOf(text: string): number | null {
  const counted =
    /\b(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)[ -]?(?:months?|mos?)\b/.exec(
      text,
    );
  if (counted?.[1]) {
    const value = NUMBER_WORDS[counted[1]] ?? Number(counted[1]);
    return Number.isFinite(value) && value > 0 ? value : null;
  }
  if (/\b(?:next|coming) month\b/.test(text)) return 1;
  if (/\bnext year\b/.test(text)) return AXIS.forecast.length;
  if (/\bhalf (?:a |the )?year\b/.test(text)) return 6;
  return null;
}

const QUARTER_ORDINALS: Record<string, number> = {
  first: 1,
  '1st': 1,
  second: 2,
  '2nd': 2,
  third: 3,
  '3rd': 3,
  fourth: 4,
  '4th': 4,
};

function quarterOf(index: number): number {
  const month = AXIS.forecast[index];
  return month ? Math.floor(month.monthIndex / 3) : 0;
}

/** The forecast months in one calendar quarter, the first time it comes round. */
function quarterWindow(
  quarter: number,
): { fromIndex: number; toIndex: number } | null {
  const fromIndex = AXIS.forecast.findIndex(
    (_, index) => quarterOf(index) === quarter,
  );
  if (fromIndex === -1) return null;
  let toIndex = fromIndex;
  while (toIndex < AXIS.forecast.length && quarterOf(toIndex) === quarter) {
    toIndex += 1;
  }
  return { fromIndex, toIndex };
}

function windowLabel(fromIndex: number, toIndex: number): string {
  const first = AXIS.forecast[fromIndex]?.label ?? '';
  const last = AXIS.forecast[toIndex - 1]?.label ?? '';
  return first === last ? first : `${first} to ${last}`;
}

interface ReadWindow {
  fromIndex: number;
  toIndex: number;
  windowText: string;
  explicitWindow: boolean;
}

function readWindow(text: string): ReadWindow {
  const quarterNumber = /\bq([1-4])\b/.exec(text)?.[1];
  const quarterWord =
    /\b(first|second|third|fourth|1st|2nd|3rd|4th) quarter\b/.exec(text)?.[1];
  const quarter = quarterNumber
    ? Number(quarterNumber) - 1
    : quarterWord
      ? (QUARTER_ORDINALS[quarterWord] ?? 1) - 1
      : null;
  if (quarter !== null) {
    const window = quarterWindow(quarter);
    if (window) {
      return {
        ...window,
        windowText: `Q${quarter + 1}`,
        explicitWindow: true,
      };
    }
  }
  if (/\bnext quarter\b/.test(text)) {
    const window = quarterWindow((quarterOf(0) + 1) % 4);
    if (window)
      return { ...window, windowText: 'next quarter', explicitWindow: true };
  }
  if (/\b(?:this|current) quarter\b/.test(text)) {
    const window = quarterWindow(quarterOf(0));
    if (window)
      return { ...window, windowText: 'this quarter', explicitWindow: true };
  }
  const months = monthsOf(text);
  const count = Math.min(months ?? DEFAULT_MONTHS, AXIS.forecast.length);
  return {
    fromIndex: 0,
    toIndex: count,
    windowText: count === 1 ? 'next month' : `the next ${count} months`,
    explicitWindow: months !== null,
  };
}

/**
 * Reads a forecast change out of a message, or null when the message is about
 * something else. A percent and a direction are required, plus either a word
 * about the forecast or sales, or months to apply it to.
 */
export function parseOverrideRequest(message: string): OverrideRequest | null {
  const text = clean(message);
  const percent = readPercent(text);
  if (!percent || percent.value <= 0) return null;
  const direction = readDirection(text, percent.sign);
  if (direction === null) return null;
  const window = readWindow(text);
  if (!SUBJECT_WORDS.test(text) && !window.explicitWindow) return null;
  return {
    percent: direction * percent.value,
    fromIndex: window.fromIndex,
    toIndex: window.toIndex,
    windowText: window.windowText,
    explicitWindow: window.explicitWindow,
  };
}

// --- Finding the item -----------------------------------------------------------------

export interface OverrideContext {
  /** Path of the page the chat is open on. */
  pathname: string;
  /** The series in the address, on the forecast details page. */
  series: string | null;
}

type Resolved = { ids: string[] } | { question: string };

function squash(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Panels and kits named in the sentence. */
function namedFinishedGoods(text: string): string[] {
  const found = new Set<string>();
  if (/\b(?:motor control|mcc)\b/.test(text)) found.add(MCC);
  if (/\b(?:power distribution|pdp)\b/.test(text)) found.add(PDP);
  if (/\b(?:kits?|retrofits?)\b/.test(text)) found.add(KIT);
  if (found.size === 0 && /\bpanels\b/.test(text)) {
    found.add(MCC);
    found.add(PDP);
  }
  const squashed = squash(text);
  for (const id of FINISHED_GOOD_SERIES) {
    if (squashed.includes(squash(id.split('@')[0] ?? id))) found.add(id);
  }
  return FINISHED_GOOD_SERIES.filter((id) => found.has(id));
}

const FAMILY_ALIASES: Array<[RegExp, string]> = [
  [/\bthhn\b/, 'THHN-1/0-CU'],
  [/\b(?:vfds?|drives?)\b/, 'VFD-50HP-480'],
  [/\bhigh[- ]?bays?\b/, 'LED-HB150-PP'],
  [/\bmccbs?\b/, 'MCCB-800A'],
];

function namedSite(text: string): string | null {
  if (/\bpanel shop\b|\bpnl\b/.test(text)) return 'PNL';
  if (/\bphoenix\b|\bphx\b/.test(text)) return 'PHX';
  if (/\bhayward\b|\bhay\b/.test(text)) return 'HAY';
  if (/\bdallas\b|\bdal\b/.test(text)) return 'DAL';
  return null;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join('');
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

const ASK_WHICH =
  'Which item should I change? Name it, such as the motor control panels or THHN-1/0-CU, or open its forecast and ask again.';

function currentSeriesOf(context: OverrideContext): string | null {
  const onDetails = context.pathname.endsWith(DETAILS_PATH);
  return onDetails ? (context.series ?? DEFAULT_SERIES_ID) : null;
}

/**
 * The item a sentence names (a panel, a series, or a part with maybe a place),
 * or null when it names none. A part sold at several places picks the one open
 * on the page, else asks.
 */
function namedTargets(text: string, current: string | null): Resolved | null {
  const panels = namedFinishedGoods(text);
  if (panels.length > 0) return { ids: panels };

  const exact = SERIES_SPECS.filter((spec) =>
    text.includes(specId(spec).toLowerCase()),
  );
  if (exact.length > 0) return { ids: exact.map(specId) };

  const squashed = squash(text);
  const alias = FAMILY_ALIASES.find(([pattern]) => pattern.test(text))?.[1];
  const sku =
    alias ??
    SERIES_SPECS.find((spec) => squashed.includes(squash(spec.sku)))?.sku ??
    null;
  if (!sku) return null;
  const site = namedSite(text);
  const found = SERIES_SPECS.filter(
    (spec) => spec.sku === sku && (site === null || spec.siteId === site),
  );
  if (found.length === 0) {
    return {
      question: `I don't see ${sku} forecast there. ${ASK_WHICH}`,
    };
  }
  const here = found.find((spec) => specId(spec) === current);
  const only = found.length === 1 ? found[0] : undefined;
  const target = here ?? only;
  if (target) return { ids: [specId(target)] };
  const places = joinNames(found.map((spec) => getSite(spec.siteId).name));
  return {
    question: `${sku} is forecast at ${places}. Which one should I change?`,
  };
}

function resolveTargets(message: string, context: OverrideContext): Resolved {
  const text = clean(message);
  const current = currentSeriesOf(context);
  const named = namedTargets(text, current);
  if (named) return named;
  if (/\bpanel shop\b|\bbuild plan\b|\bbuilds\b/.test(text)) {
    return { ids: FINISHED_GOOD_SERIES };
  }
  if (current) return { ids: [current] };
  if (context.pathname.endsWith(BOM_PATH)) return { ids: FINISHED_GOOD_SERIES };
  return { question: ASK_WHICH };
}

// --- Planning the change --------------------------------------------------------------

export type OverrideDestination =
  | { to: 'details'; series: string }
  | { to: 'bom' };

export type OverridePlan =
  /**
   * Nami answers and changes nothing: an item to name, or nothing to change.
   * `awaitingItem` is set when the next message can name the item.
   */
  | { kind: 'reply'; narration: string; awaitingItem?: boolean }
  | {
      kind: 'apply';
      narration: string;
      request: UpliftRequest;
      /** Where to open the change; null when the page is already open. */
      destination: OverrideDestination | null;
    };

interface SeriesChange {
  id: string;
  spec: SeriesSpec;
  before: number[];
  after: number[];
}

function describeItem(spec: SeriesSpec): string {
  const id = specId(spec);
  const name = FINISHED_GOOD_NAME[id];
  return name
    ? `the ${name} (${spec.sku})`
    : `${spec.sku} at ${getSite(spec.siteId).name}`;
}

function changeFor(
  spec: SeriesSpec,
  request: OverrideRequest,
  state: ForecastDemoState,
): SeriesChange {
  const view = buildView(spec, state);
  const factor = 1 + request.percent / 100;
  // The change applies to the model forecast, replacing months fixed inside it.
  const pins = new Map(
    Object.entries(view.pins)
      .map(([index, value]): [number, number] => [Number(index), value])
      .filter(
        ([index]) => index < request.fromIndex || index >= request.toIndex,
      ),
  );
  return {
    id: view.id,
    spec,
    before: view.adjusted,
    after: adjustForecast(view.result.forecast, {
      factor,
      fromIndex: request.fromIndex,
      toIndex: request.toIndex,
      pins,
    }),
  };
}

function windowSum(values: number[], request: OverrideRequest): number {
  return sum(values.slice(request.fromIndex, request.toIndex));
}

/** What the parts plan would order before and after, when panels or kits change. */
function partsPlanSentence(
  changes: SeriesChange[],
  state: ForecastDemoState,
  swapApproved: boolean,
): string {
  const touched = changes.filter((change) =>
    FINISHED_GOOD_SERIES.includes(change.id),
  );
  if (touched.length === 0) return '';
  const current = buildsPlanFor(state).builds;
  const replaced = new Map(
    touched.map((change) => [
      change.spec.sku,
      change.after.slice(0, BUCKET_COUNT),
    ]),
  );
  const next = fgRecord((sku) => replaced.get(sku) ?? current[sku]);
  const before = buildBomModel(current, swapApproved);
  const after = buildBomModel(next, swapApproved);
  const sameOrders = before.releasable.length === after.releasable.length;
  const sameSpend = before.plannedValue === after.plannedValue;
  if (sameOrders && sameSpend) return '';
  const orders = sameOrders
    ? `the orders to place now stay at ${after.releasable.length}`
    : `the orders to place now go from ${before.releasable.length} to ${after.releasable.length}`;
  const spend = sameSpend
    ? ''
    : `planned spend goes from ${fmtMoneyCompact(before.plannedValue)} to ${fmtMoneyCompact(after.plannedValue)}`;
  return `On the parts plan, ${[orders, spend].filter(Boolean).join(' and ')}.`;
}

/**
 * Finds the item, works out the numbers and the change to apply. `swapApproved`
 * is the parts plan's alternate-breaker state, which its orders depend on.
 */
export function planOverride(
  message: string,
  request: OverrideRequest,
  context: OverrideContext,
  state: ForecastDemoState,
  swapApproved: boolean,
): OverridePlan {
  const limited = limitReply(request);
  if (limited) return limited;
  const resolved = resolveTargets(message, context);
  if ('question' in resolved) {
    return { kind: 'reply', narration: resolved.question, awaitingItem: true };
  }
  return planFor(message, request, resolved.ids, context, state, swapApproved);
}

/**
 * The answer to "which item?": a message that names the item completes the
 * change that was asked for. Null when the message names no item.
 */
export function planFollowUp(
  asked: { message: string; request: OverrideRequest },
  answer: string,
  context: OverrideContext,
  state: ForecastDemoState,
  swapApproved: boolean,
): OverridePlan | null {
  const named = namedTargets(clean(answer), currentSeriesOf(context));
  if (!named) return null;
  if ('question' in named) {
    return { kind: 'reply', narration: named.question, awaitingItem: true };
  }
  return planFor(
    asked.message,
    asked.request,
    named.ids,
    context,
    state,
    swapApproved,
  );
}

function limitReply(request: OverrideRequest): OverridePlan | null {
  const limit = request.percent > 0 ? MAX_UPLIFT_PERCENT : MAX_CUT_PERCENT;
  if (Math.abs(request.percent) <= limit) return null;
  return {
    kind: 'reply',
    narration: `I can ${request.percent > 0 ? 'raise' : 'lower'} the forecast by up to ${limit}% in one go. Tell me a smaller number and I'll apply it.`,
  };
}

function planFor(
  message: string,
  request: OverrideRequest,
  ids: string[],
  context: OverrideContext,
  state: ForecastDemoState,
  swapApproved: boolean,
): OverridePlan {
  const specs = ids.flatMap((id) => {
    const spec = getSpec(id);
    return spec ? [spec] : [];
  });
  const changes = specs.map((spec) => changeFor(spec, request, state));
  const before = sum(
    changes.map((change) => windowSum(change.before, request)),
  );
  const after = sum(changes.map((change) => windowSum(change.after, request)));
  const months = windowLabel(request.fromIndex, request.toIndex);
  const percentText = `${Math.abs(request.percent)}%`;
  if (changes.every((change) => change.before.join() === change.after.join())) {
    return {
      kind: 'reply',
      narration: `The forecast already reads ${formatFactorPercent(1 + request.percent / 100)} for ${months}, so there is nothing to change.`,
    };
  }

  const first = changes[0];
  const single = changes.length === 1 && first !== undefined;
  const unit = first?.spec.unit ?? 'each';
  const quantity = (value: number) =>
    single ? fmtUnits(value, unit) : `${value.toLocaleString('en-US')} builds`;
  const items = joinNames(changes.map((change) => describeItem(change.spec)));
  const gap = Math.abs(after - before);
  const gapText = single
    ? fmtUnits(gap, unit)
    : `${gap.toLocaleString('en-US')} builds`;
  const sentences = [
    `Got it. I'll ${request.percent > 0 ? 'raise' : 'lower'} ${items} ${percentText} ${request.percent > 0 ? 'above' : 'below'} the model forecast for ${request.windowText} (${months}): ${quantity(before)} becomes ${quantity(after)}, ${gapText} ${after >= before ? 'more' : 'less'}.`,
    partsPlanSentence(changes, state, swapApproved),
  ];

  const needsSignOff = changes.some(
    (change) => buildView(change.spec, state).approval.status === 'approved',
  );
  sentences.push(
    needsSignOff
      ? 'This changes the forecast, so it goes to approval before stock levels follow it.'
      : 'It stays in the approval queue before stock levels follow it.',
  );

  const onBom = context.pathname.endsWith(BOM_PATH);
  const currentSeries = currentSeriesOf(context);
  let destination: OverrideDestination | null;
  if (single) {
    destination =
      first.id === currentSeries ? null : { to: 'details', series: first.id };
  } else {
    destination = onBom ? null : { to: 'bom' };
  }
  sentences.push(
    destination === null
      ? onBom
        ? 'The builds and parts to order on this page change as it applies.'
        : 'The grid shows your change and the chart shows the adjusted line.'
      : destination.to === 'bom'
        ? "I've opened the parts plan so you can see the builds and orders change."
        : "I've opened the forecast so you can see the change.",
  );

  return {
    kind: 'apply',
    narration: sentences.filter(Boolean).join(' '),
    request: {
      seriesIds: changes.map((change) => change.id),
      percent: request.percent,
      fromIndex: request.fromIndex,
      toIndex: request.toIndex,
      reason: message.trim().slice(0, 140),
      via: 'Nami',
    },
    destination,
  };
}

/** The series in a page address, when it names one. */
export function seriesFromSearch(search: unknown): string | null {
  if (typeof search !== 'object' || search === null) return null;
  if (!('series' in search)) return null;
  return typeof search.series === 'string' ? search.series : null;
}
